import { type FC, useEffect, useState, useCallback, useRef } from 'react';

interface SpotlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TourOverlayProps {
  targetEl: HTMLElement | null;
  padding: number;
  zIndex: number;
  clickable: boolean;
  /** Show only the dimmed overlay with no spotlight cutout */
  dimOnly?: boolean;
  onOverlayClick: () => void;
}

const BORDER_RADIUS = 8;
const TRANSITION_DURATION = '300ms';

const TourOverlay: FC<TourOverlayProps> = ({
  targetEl,
  padding,
  zIndex,
  clickable,
  dimOnly = false,
  onOverlayClick,
}) => {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const rafRef = useRef<number>(0);

  const updateRect = useCallback(() => {
    if (!targetEl) {
      setRect(null);
      return;
    }
    const r = targetEl.getBoundingClientRect();
    setRect({
      x: r.left - padding,
      y: r.top - padding,
      width: r.width + padding * 2,
      height: r.height + padding * 2,
    });
  }, [targetEl, padding]);

  useEffect(() => {
    updateRect();

    if (!targetEl) return;

    // Reposition on resize/scroll
    const handleReposition = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateRect);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    // ResizeObserver for dynamic size changes
    const resizeObserver = new ResizeObserver(handleReposition);
    resizeObserver.observe(targetEl);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      resizeObserver.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [targetEl, updateRect]);

  if (!rect && !dimOnly) return null;

  return (
    <>
      {/* SVG overlay with mask cutout (or full dim when dimOnly) */}
      <svg
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex,
          pointerEvents: 'none',
        }}
        data-cy="tour-overlay"
      >
        {rect && !dimOnly ? (
          <>
            <defs>
              <mask id="tour-spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  rx={BORDER_RADIUS}
                  ry={BORDER_RADIUS}
                  fill="black"
                  style={{ transition: `all ${TRANSITION_DURATION} ease-in-out` }}
                />
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.5)"
              mask="url(#tour-spotlight-mask)"
            />
          </>
        ) : (
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0, 0, 0, 0.5)" />
        )}
      </svg>

      {/* Click-blocking overlay (everything except spotlight area) */}
      <div
        onClick={onOverlayClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: zIndex + 1,
          cursor: 'default',
        }}
        data-cy="tour-overlay-click-blocker"
      />

      {/* Spotlight click-through area (only if clickable and not dimOnly) */}
      {rect && !dimOnly && (
        <div
          style={{
            position: 'fixed',
            left: rect.x,
            top: rect.y,
            width: rect.width,
            height: rect.height,
            zIndex: zIndex + 2,
            borderRadius: BORDER_RADIUS,
            pointerEvents: clickable ? 'none' : 'auto',
            transition: `all ${TRANSITION_DURATION} ease-in-out`,
          }}
          data-cy="tour-spotlight-area"
        />
      )}
    </>
  );
};

export default TourOverlay;
